import React from "react";
import { cn } from "../../../lib/helpers";
import { ImageBuilder } from "../../global/image-builder";
import pipelineHeroQuizData from "./pipeline-hero-quiz.json";

// Helper to parse bold text within a line
const parseBoldText = (line) => {
  const parts = [];
  let lastIndex = 0;
  const boldRegex = /\*([^*]+)\*/g;
  let match;

  while ((match = boldRegex.exec(line)) !== null) {
    // Add text before the bold part
    if (match.index > lastIndex) {
      parts.push(line.substring(lastIndex, match.index));
    }
    // Add the bold part
    parts.push(<strong key={`bold-${match.index}`}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last bold part
  if (lastIndex < line.length) {
    parts.push(line.substring(lastIndex));
  }

  return parts.length > 0 ? parts : line;
};

// Quiz Share Card Component (official version)
export const QuizShareCard = ({ data }) => {
  // Use passed-in config or fallback to default import
  const heroConfig = pipelineHeroQuizData;

  // Generic resolver: match archetype by key or name (case-insensitive, partial match)
  const resolveArchetype = (name) => {
    if (!heroConfig?.archetypes) return undefined;
    const n = (name || '').toLowerCase();
    const { archetypes } = heroConfig;

    // First try direct key match (most reliable for quiz results)
    if (archetypes[n]) {
      return archetypes[n];
    }

    // Then try matching by display name or aliases
    return Object.values(archetypes).find(a => {
      if (!a) return false;
      return (
        (a.name && a.name.toLowerCase() === n) ||
        (a.key && a.key.toLowerCase() === n) ||
        (a.aliases && a.aliases.some(alias => alias.toLowerCase() === n)) ||
        (a.name && n.includes(a.name.toLowerCase())) ||
        // Handle flashpoint -> striker mapping
        (n === 'flashpoint' && a.name?.toLowerCase().includes('striker'))
      );
    });
  };

  // Use pre-resolved archetype if available, otherwise resolve from data.archetype
  const archetype = data?.resolvedArchetype || resolveArchetype(data?.archetype);

  const getEmailDomain = () => (data?.email ? data.email.split('@')[1] : null);

  const shareText = data?.content?.share || data?.content || null;

  return (
    <div
      className="space-y-6 rounded-2xl font-openSans"
      style={{ backgroundColor: archetype?.borderColor, padding: "5px" }}
    >
      <div>
        {/* Hero Squad Graphic */}
        <div className="flex items-center justify-center rounded-t-xl bg-dark-20 overflow-hidden" style={{ minHeight: '200px' }}>
          <ImageBuilder
            src={archetype?.squadImage}
            alt={`${archetype?.name || 'Hero'} squad graphic`}
          />
        </div>
        <div className="bg-dark-30 rounded-b-xl relative">
          {/* Power Icon */}
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-20">
            <div
              className="p-1 rounded-2xl flex items-center justify-center shadow-lg border-4 bg-black"
              style={{ borderColor: archetype?.borderColor }}
            >
              <ImageBuilder
                src={`/img/quiz/${archetype.powerIcon}`}
                alt={`${archetype.name || 'Power'} icon`}
                className="w-12 h-12"
              />
            </div>
          </div>
          {/* Name & Subtitle */}
          <div className="pt-12 text-5xl text-white font-superhero uppercase text-center italic">
            {archetype?.name || data?.archetype}
          </div>
          <div className="text-center font-superhero font-light uppercase text-3xl italic">
            {archetype?.subtitle || ""}
          </div>
          {/* Share Text */}
          {shareText && (
            <div className="px-3 md:px-6 pt-6 py-8 leading-snug text-center text-light-80">
              {parseBoldText(shareText)}
            </div>
          )}
          {/* Facts Grid */}
          {data?.org?.facts?.name || data?.email || data?.seniority || data?.dept || data?.persona ? (
            <div className="p-6 pt-0 font-base">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  {data?.org?.facts?.name ? (
                    <div>
                      <p className="text-light-60 text-sm font-medium">Company</p>
                      <div className="text-white mt-1 text-lg">{data.org.facts.name}</div>
                    </div>
                  ) : data?.email ? (
                    <div>
                      <p className="text-light-60 text-sm font-medium">Domain</p>
                      <div className="text-white mt-1">{getEmailDomain()}</div>
                    </div>
                  ) : <div>
                    <p className="text-light-60 text-sm font-medium">Role</p>
                    <div className="text-white mt-1">{data?.seniority}</div>
                  </div>}
                </div>
                <div className="text-center">
                  <p className="text-light-60 text-sm font-medium">Department</p>
                  <div className="text-white mt-1 text-lg">{data?.dept || data?.persona}</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
